/**
 * Helpers pour dériver le statut global d'une weekly_sheet depuis day_statuses.
 *
 * Le modèle : day_statuses est la source de vérité par jour. Le champ
 * `weekly_sheets.status` reste stocké en DB pour rétrocompat avec les
 * filtres paie/reporting existants, mais il est DÉRIVÉ :
 *
 *   - 'rejected' : si rejection_reason non vide (indépendant de day_statuses)
 *   - 'validated': si TOUS les jours non-vides sont 'validated' (sheet figée)
 *   - 'pending'  : si au moins 1 jour est 'submitted' (en attente admin)
 *   - 'draft'    : sinon
 *
 * Ainsi, quand admin valide uniquement mars d'une semaine à cheval :
 *   - day_statuses mars → 'validated', avril reste 'draft'
 *   - computeSheetStatus → 'pending' (pas tout validé)
 *   - RLS 'validated=figé' ne s'active pas → employé peut saisir avril
 *
 * Quand tout est validé :
 *   - tous les days → 'validated'
 *   - computeSheetStatus → 'validated' → RLS fige la sheet
 */

export type DayStatusValue = 'draft' | 'submitted' | 'validated';
export type DayStatuses = Record<string, DayStatusValue>;
export type SheetStatus = 'draft' | 'pending' | 'validated' | 'rejected';

/**
 * Dérive le statut global depuis day_statuses + rejection_reason.
 */
export function computeSheetStatus(
  dayStatuses: DayStatuses | null | undefined,
  rejectionReason: string | null | undefined,
): SheetStatus {
  if (rejectionReason && rejectionReason.trim().length > 0) {
    return 'rejected';
  }

  const values = Object.values(dayStatuses ?? {});
  if (values.length === 0) return 'draft';

  // Tous validés → feuille figée
  if (values.every((v) => v === 'validated')) {
    return 'validated';
  }
  // Au moins 1 submitted → admin a du travail à faire
  if (values.some((v) => v === 'submitted')) {
    return 'pending';
  }
  // Sinon : mix validated+draft, ou que des draft.
  // Sémantique : pas de travail admin immédiat. C'est soit un brouillon employé,
  // soit une semaine à cheval où admin a validé un mois et attend la saisie
  // du mois suivant. Dans les deux cas → 'draft' (hors liste AdminValidate).
  return 'draft';
}

/**
 * Met à jour day_statuses pour marquer les jours d'un mois donné comme 'validated'.
 *
 * @param dayStatuses - état courant
 * @param monthKey    - clé mois 'YYYY-MM' (ex: '2026-03')
 * @returns nouvelle map day_statuses avec les jours du mois passés à 'validated'.
 *          Les jours 'draft' du mois restent 'draft' (un jour non-soumis ne peut
 *          pas être validé). Les jours 'submitted' ou 'validated' deviennent 'validated'.
 */
export function validateMonthInDayStatuses(
  dayStatuses: DayStatuses,
  monthKey: string,
): DayStatuses {
  const result: DayStatuses = { ...dayStatuses };
  for (const [dateStr, status] of Object.entries(result)) {
    if (dateStr.startsWith(monthKey) && status !== 'draft') {
      result[dateStr] = 'validated';
    }
  }
  return result;
}

/**
 * Met à jour day_statuses pour marquer TOUS les jours 'submitted' (et 'validated')
 * comme 'validated'. Les jours 'draft' restent 'draft'.
 */
export function validateAllSubmittedDays(dayStatuses: DayStatuses): DayStatuses {
  const result: DayStatuses = { ...dayStatuses };
  for (const [dateStr, status] of Object.entries(result)) {
    if (status === 'submitted') {
      result[dateStr] = 'validated';
    }
  }
  return result;
}

/**
 * Formate une clé mois 'YYYY-MM' en libellé court français.
 * Ex: '2026-03' → 'mars 2026'
 *     '2026-12' → 'déc. 2026'
 *
 * Utilisé dans les boutons admin "Valider mars 2026 (2j)" qui doivent
 * rester compacts tout en étant lisibles.
 */
const MONTH_NAMES_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

export function formatMonthFr(monthKey: string): string {
  const match = monthKey.match(/^(\d{4})-(\d{2})$/);
  if (!match) return monthKey;
  const year = match[1];
  const monthIdx = parseInt(match[2], 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return monthKey;
  return `${MONTH_NAMES_FR[monthIdx]} ${year}`;
}

/**
 * Retourne la liste des mois (clés 'YYYY-MM') présents dans day_statuses,
 * avec pour chacun un résumé : nb de jours total, soumis, validés, brouillons.
 */
export function summarizeMonths(
  dayStatuses: DayStatuses,
): { monthKey: string; total: number; submitted: number; validated: number; draft: number }[] {
  const map: Record<string, { total: number; submitted: number; validated: number; draft: number }> = {};
  for (const [dateStr, status] of Object.entries(dayStatuses)) {
    const monthKey = dateStr.slice(0, 7);
    if (!map[monthKey]) map[monthKey] = { total: 0, submitted: 0, validated: 0, draft: 0 };
    map[monthKey].total++;
    if (status === 'submitted') map[monthKey].submitted++;
    else if (status === 'validated') map[monthKey].validated++;
    else map[monthKey].draft++;
  }
  return Object.entries(map)
    .map(([monthKey, counts]) => ({ monthKey, ...counts }))
    .sort((a, b) => a.monthKey.localeCompare(b.monthKey));
}
